import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronLeft,
  Cpu,
  Database,
  HardDrive,
  Lock,
  MemoryStick,
  Minus,
  Plus,
  Power,
  Receipt,
  RefreshCw,
  Server,
  XCircle,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useConfigChangeLimits,
  useConfigChangeQuote,
  useExecuteConfigChange,
  useResourcePowerAction,
} from "@/hooks/useResourceQueries";
import { formatCurrency } from "@/lib/currency";
import { cn, formatDateTime } from "@/lib/utils";
import type {
  ConfigChangeOutcome,
  ResourceConfigChangeBlock,
  ResourceConfigChangeExecution,
  ResourceConfigChangeLimits,
  ResourceConfigChangeQuote,
  ResourceConfigChangeTarget,
  ResourceConfigSnapshot,
  ResourceSpecMismatch,
} from "@/types/admin";

interface ResourceConfigChangeDialogProps {
  /** The VM whose configuration is being changed. */
  resourceId: string;
  /** Name shown in the heading, so the dialog identifies the VM before anything loads. */
  resourceName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** What the operator typed, before it becomes a target for the server. */
interface DraftConfig {
  cores: string;
  memoryMb: string;
  /** Device name to the size typed for it, in GB. */
  disks: Record<string, string>;
}

/** One line of "this is what you are about to change", worked out without asking the server. */
interface PendingChange {
  key: string;
  label: string;
  unit: string | null;
  from: number;
  to: number;
  Icon: typeof Cpu;
}

/**
 * The three screens this dialog moves through.
 *
 * The split between `configure` and `confirm` is the whole shape. Nothing is priced while the
 * operator is still typing — every quote is a live call to a provider the operational notes
 * describe as slow and intermittently silent, and a figure that keeps moving under the cursor is
 * not one anybody can read out to a customer. They set the configuration, ask for the price once,
 * and confirm that exact figure.
 *
 * Confirming does the whole job in one request: the VM is changed, the record is corrected from
 * what actually landed, and the price moves with it. There is deliberately no state in which the
 * infrastructure has moved and WiCloud has not — that state existed before, and walking away from
 * it left the customer's next renewal billing for a machine they no longer had.
 */
type Phase = "configure" | "confirm" | "running" | "done";

const BLOCK_ICONS: Record<string, typeof Cpu> = {
  Cores: Cpu,
  Memory: MemoryStick,
  Disk: HardDrive,
};

/** vCPU counts worth one press. Anything else inside the plan's range is still typeable. */
const CPU_STEPS = [1, 2, 4, 8, 16, 32];

/** Memory sizes worth one press, in MB. */
const MEMORY_STEPS_MB = [1024, 2048, 4096, 8192, 16384, 32768, 65536, 131072];

/** Disk sizes worth one press, in GB. */
const DISK_STEPS_GB = [20, 40, 60, 80, 100, 150, 200, 300, 500, 1000, 2000];

/**
 * How often, and how many times, the infrastructure is re-read after it accepted a shutdown. A
 * graceful shutdown is accepted before the guest has powered off, so the first reads still say
 * "running".
 */
const STOP_RECHECK_MS = 4000;
const STOP_RECHECK_ATTEMPTS = 8;

/** A figure with its unit, or an em dash when there is nothing to show. */
function formatValue(
  value: number | null | undefined,
  unit?: string | null,
): string {
  if (value === null || value === undefined) return "—";
  return unit ? `${value} ${unit}` : `${value}`;
}

/** A signed money figure, so a credit reads as one rather than as a smaller charge. */
function formatDelta(amount: number, currency: string): string {
  if (amount === 0) return formatCurrency(0, currency);
  const sign = amount > 0 ? "+" : "−";
  return `${sign}${formatCurrency(Math.abs(amount), currency)}`;
}

/** Reads a typed figure. Anything unparseable is not a number the server should be sent. */
function parsePositive(value: string): number | undefined {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

/** The form as the server wants it: a destination, never a list of operations. */
function targetFromDraft(draft: DraftConfig): ResourceConfigChangeTarget {
  return {
    cores: parsePositive(draft.cores),
    memoryMb: parsePositive(draft.memoryMb),
    disks: Object.fromEntries(
      Object.entries(draft.disks)
        .map(([device, size]) => [device, parsePositive(size)] as const)
        .filter(
          (entry): entry is readonly [string, number] => entry[1] !== undefined,
        ),
    ),
  };
}

/** The form as it stands when the dialog opens: exactly what the infrastructure reports. */
function draftFromSnapshot(snapshot: ResourceConfigSnapshot): DraftConfig {
  return {
    cores: String(snapshot.cores),
    memoryMb: String(snapshot.memoryMb),
    disks: Object.fromEntries(
      snapshot.disks.map((disk) => [disk.device, String(disk.sizeGb)]),
    ),
  };
}

/**
 * The form after a re-read that must not lose the operator's work: the fresh figures, with every
 * disk size they had already raised put back. vCPU and memory are taken fresh — the re-reads that
 * keep the form happen while those two are locked.
 */
function carryDiskEdits(
  snapshot: ResourceConfigSnapshot,
  previous: DraftConfig,
): DraftConfig {
  const fresh = draftFromSnapshot(snapshot);

  for (const disk of snapshot.disks) {
    const typed = parsePositive(previous.disks[disk.device] ?? "");
    if (typed !== undefined && typed > disk.sizeGb) {
      fresh.disks[disk.device] = String(typed);
    }
  }

  return fresh;
}

/** The listed steps inside a range, with the current value among them, smallest first. */
function stepsWithin(
  steps: number[],
  min: number,
  max: number,
  current: number,
): number[] {
  const inRange = steps.filter((step) => step >= min && step <= max);
  return [...new Set([...inRange, current])].sort((a, b) => a - b);
}

/** Every disk as the form stands, add-on disks included: what the plan's disk range is held to. */
function draftDiskTotal(
  current: ResourceConfigSnapshot,
  draft: DraftConfig,
): number {
  return current.disks.reduce(
    (total, disk) =>
      total + (parsePositive(draft.disks[disk.device] ?? "") ?? disk.sizeGb),
    0,
  );
}

/**
 * What the operator has actually changed, worked out from the form rather than from the server.
 *
 * The server still decides what a change is — it re-reads the VM before applying anything. This is
 * only so the screen can say "you are changing two things" before a single call is made.
 */
function pendingChanges(
  current: ResourceConfigSnapshot,
  draft: DraftConfig,
): PendingChange[] {
  const changes: PendingChange[] = [];

  const cores = parsePositive(draft.cores);
  if (cores !== undefined && cores !== current.cores) {
    changes.push({
      key: "cores",
      label: "vCPU",
      unit: null,
      from: current.cores,
      to: cores,
      Icon: Cpu,
    });
  }

  const memory = parsePositive(draft.memoryMb);
  if (memory !== undefined && memory !== current.memoryMb) {
    changes.push({
      key: "memory",
      label: "Memory",
      unit: "MB",
      from: current.memoryMb,
      to: memory,
      Icon: MemoryStick,
    });
  }

  for (const disk of current.disks) {
    const size = parsePositive(draft.disks[disk.device] ?? "");
    if (size !== undefined && size !== disk.sizeGb) {
      changes.push({
        key: `disk:${disk.device}`,
        label: `Disk ${disk.device}`,
        unit: "GB",
        from: disk.sizeGb,
        to: size,
        Icon: HardDrive,
      });
    }
  }

  return changes;
}

/**
 * The form's own refusals — the ones that can be decided without asking the infrastructure.
 *
 * The ceilings are the product line's, read once from the limits endpoint, so an operator is not
 * sent on a provider round trip to be told the plan stops at 32 vCPU or that a disk cannot shrink.
 * The server checks the same range again before anything is applied.
 */
function validate(
  current: ResourceConfigSnapshot,
  draft: DraftConfig,
  limits: ResourceConfigChangeLimits | null,
): Record<string, string> {
  const errors: Record<string, string> = {};

  const cores = parsePositive(draft.cores);
  if (cores === undefined) {
    errors.cores = "Enter a whole number of vCPUs, at least 1.";
  } else if (limits && (cores < limits.cores.min || cores > limits.cores.max)) {
    errors.cores = `This plan allows ${limits.cores.min} to ${limits.cores.max} vCPU.`;
  }

  const memoryMb = parsePositive(draft.memoryMb);
  if (memoryMb === undefined) {
    errors.memoryMb = "Enter the memory in MB, at least 1.";
  } else if (
    limits &&
    (memoryMb < limits.memoryGb.min * 1024 ||
      memoryMb > limits.memoryGb.max * 1024)
  ) {
    errors.memoryMb = `This plan allows ${limits.memoryGb.min} to ${limits.memoryGb.max} GB of memory.`;
  }

  const diskTotal = draftDiskTotal(current, draft);
  const overPlan = limits !== null && diskTotal > limits.diskGb.max;

  for (const disk of current.disks) {
    const raw = draft.disks[disk.device] ?? "";
    const size = parsePositive(raw);

    if (size === undefined) {
      errors[disk.device] = "Enter a size in GB.";
    } else if (size < disk.sizeGb) {
      // Storage cannot shrink a volume; a smaller total means removing a disk, not resizing one.
      errors[disk.device] =
        `Cannot go below ${disk.sizeGb} GB — storage only grows.`;
    } else if (overPlan && size > disk.sizeGb) {
      errors[disk.device] =
        `All disks together would be ${diskTotal} GB; this plan allows ${limits.diskGb.max} GB.`;
    }
  }

  return errors;
}

function messageOf(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

/**
 * Changes a VM's vCPU, memory and disk capacity, then records what landed and moves the resource
 * onto the matching price — all behind one confirmation.
 *
 * The rules it enforces come from the provider, not from taste
 * (`docs/wiservice-vm-spec-change-rules.md`): disks grow only, and vCPU and memory move in either
 * direction but only on a stopped VM — a running one answers `success: false` and changes nothing.
 * One rule is WiCloud's own: a disk bought as its own storage resource carries its own price, so it
 * is shown here but cannot be resized from this screen.
 */
export function ResourceConfigChangeDialog({
  resourceId,
  resourceName,
  open,
  onOpenChange,
}: ResourceConfigChangeDialogProps) {
  const [phase, setPhase] = useState<Phase>("configure");
  const [draft, setDraft] = useState<DraftConfig | null>(null);

  // The configuration the infrastructure reports, read once when the dialog opens. The form is
  // drawn from it and every "currently N" on screen is it.
  const [baseline, setBaseline] = useState<ResourceConfigChangeQuote | null>(
    null,
  );

  // The priced target — only ever the one the operator asked for by pressing the button, which is
  // what makes the figure on the confirm screen the same one they are about to commit to.
  const [quote, setQuote] = useState<ResourceConfigChangeQuote | null>(null);

  const [execution, setExecution] =
    useState<ResourceConfigChangeExecution | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [priceError, setPriceError] = useState<string | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  // Bumped on every read of the live configuration. A reply belonging to an earlier read is dropped
  // rather than written over the current one.
  const readingRef = useRef(0);

  const quoteMutation = useConfigChangeQuote();
  const executeChange = useExecuteConfigChange();

  // The plan's range. The form is not drawn without it: a control the range does not bound would
  // only be refused once the operator asked for a price.
  const limitsQuery = useConfigChangeLimits(resourceId, open);
  const limits = limitsQuery.data ?? null;

  // Shutting the VM down from here. `stoppingRef` is set in the same tick as the click, so a double
  // click cannot send the shutdown twice; `aliveRef` stops the re-reads once the dialog has gone.
  const shutDownMutation = useResourcePowerAction().mutateAsync;
  const [stopping, setStopping] = useState(false);
  const [powerNote, setPowerNote] = useState<string | null>(null);
  const stoppingRef = useRef(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // Held in a ref so the effect below depends on nothing that can change while the operator types.
  // A re-run of that effect would wipe the form mid-edit.
  const quoteRef = useRef(quoteMutation.mutateAsync);
  quoteRef.current = quoteMutation.mutateAsync;

  /**
   * Reads the live configuration with an empty target. The call that prices a change is also the
   * one that knows the per-device disk layout the form is drawn from, so this costs exactly one
   * round trip — and it is the only one made before the operator asks for a price.
   *
   * With `keepForm` the form stays on screen and keeps the disk sizes already raised: that is the
   * re-read behind the power-state check, which must not throw the operator's work away. Answers
   * whether the infrastructure reports the VM running, or null when it could not be read.
   */
  const readLiveConfiguration = useCallback(
    async (keepForm = false): Promise<boolean | null> => {
      const reading = readingRef.current + 1;
      readingRef.current = reading;

      if (!keepForm) {
        setDraft(null);
        setBaseline(null);
      }
      setLoadError(null);
      setPriceError(null);

      try {
        const result = await quoteRef.current({ id: resourceId, target: {} });
        if (readingRef.current !== reading) return null;

        setBaseline(result);
        setDraft((previous) =>
          keepForm && previous
            ? carryDiskEdits(result.current, previous)
            : draftFromSnapshot(result.current),
        );
        return result.providerReportsRunning;
      } catch (error) {
        if (readingRef.current !== reading) return null;

        // A re-read that keeps the form leaves it standing; the caller says the read failed.
        if (!keepForm) {
          setLoadError(
            messageOf(error, "The configuration could not be read."),
          );
        }
        return null;
      }
    },
    [resourceId],
  );

  /** Re-reads the power state without losing the form. */
  const recheckPowerState = useCallback(async () => {
    const stillRunning = await readLiveConfiguration(true);
    if (!aliveRef.current) return;

    setPowerNote(
      stillRunning === null
        ? "The infrastructure did not answer. Re-check again in a moment."
        : null,
    );
  }, [readLiveConfiguration]);

  /**
   * Shuts the VM down gracefully, then re-reads the infrastructure until it stops reporting the VM
   * running — the gate on vCPU and memory is what the provider reports, not the status WiCloud
   * wrote when the shutdown was accepted.
   */
  const shutDownVm = useCallback(async () => {
    if (stoppingRef.current) {
      return;
    }

    stoppingRef.current = true;
    setStopping(true);
    setPowerNote(null);

    try {
      await shutDownMutation({ id: resourceId, action: "shutdown" });
    } catch {
      // The hook's own toast has said why; the button is offered again.
      stoppingRef.current = false;
      setStopping(false);
      return;
    }

    let stillRunning: boolean | null = true;
    for (
      let attempt = 0;
      attempt < STOP_RECHECK_ATTEMPTS && stillRunning !== false;
      attempt += 1
    ) {
      await new Promise((resolve) => setTimeout(resolve, STOP_RECHECK_MS));
      if (!aliveRef.current) return;

      stillRunning = await readLiveConfiguration(true);
    }

    if (!aliveRef.current) return;

    stoppingRef.current = false;
    setStopping(false);
    if (stillRunning !== false) {
      setPowerNote(
        "The shutdown was accepted, but the infrastructure still reports the VM running. Give the guest a moment, then re-check.",
      );
    }
  }, [readLiveConfiguration, resourceId, shutDownMutation]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setPhase("configure");
    setQuote(null);
    setExecution(null);
    setRunError(null);
    setPowerNote(null);

    void readLiveConfiguration();
  }, [open, readLiveConfiguration]);

  const current = baseline?.current ?? null;

  const errors = useMemo(
    () => (current && draft ? validate(current, draft, limits) : {}),
    [current, draft, limits],
  );

  const changes = useMemo(
    () => (current && draft ? pendingChanges(current, draft) : []),
    [current, draft],
  );

  const hasErrors = Object.keys(errors).length > 0;
  const canPrice = changes.length > 0 && !hasErrors && !quoteMutation.isPending;

  /** Asks what the chosen configuration would cost. One call, on purpose, when asked for. */
  const priceChange = useCallback(async () => {
    if (!draft) {
      return;
    }

    setPriceError(null);

    try {
      const priced = await quoteRef.current({
        id: resourceId,
        target: targetFromDraft(draft),
      });
      setQuote(priced);
      setPhase("confirm");
    } catch (error) {
      // Stay on the form and keep no stale price around: a figure from a target that was refused is
      // worse than none.
      setQuote(null);
      setPriceError(
        messageOf(error, "That configuration could not be priced."),
      );
    }
  }, [draft, resourceId]);

  /**
   * One request that finishes the job: the VM is changed, the record corrected from what actually
   * landed, and the resource and its billing cycle moved onto the matching price.
   */
  const confirmChange = useCallback(async () => {
    if (!draft) {
      return;
    }

    setRunError(null);
    setPhase("running");

    try {
      const result = await executeChange.mutateAsync({
        id: resourceId,
        target: targetFromDraft(draft),
      });

      setExecution(result);
      setPhase("done");
    } catch (error) {
      // The request was refused before anything reached the provider, so the form is still the
      // right place to be. A run that changed the VM and then stopped resolves instead, with its
      // own outcome.
      setRunError(messageOf(error, "The configuration change was refused."));
      setPhase("confirm");
    }
  }, [draft, executeChange, resourceId]);

  const title = baseline?.resourceName ?? resourceName;
  const busy = phase === "running";

  // The provider refuses a vCPU or memory change on a running VM: it answers success:false and
  // changes nothing. The quote says so per block, and the confirm button is closed off rather than
  // left to be pressed into a refusal.
  const blockedByPowerState =
    quote?.blocks.find((block) => !block.canApply) ?? null;

  // An empty block list is NOT a reason to close the button off. It means the VM is already at the
  // target — which is what a retry of an unfinished run looks like — and confirming is then how the
  // record and the price get written. The server treats that case as "finish it", not as a no-op.
  const canConfirm = quote !== null && blockedByPowerState === null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Never interruptible mid-call: a half-sent request has no useful state to return to.
        if (busy) {
          return;
        }

        onOpenChange(next);
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            Change configuration{title ? ` · ${title}` : ""}
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <DialogDescription>
            Sets the VM's vCPU, memory and disk capacity, then records what
            landed and moves the resource onto the matching monthly price. Disks
            grow only; vCPU and memory move either way but need the VM stopped.
            No payment is taken — the figure shown is what to quote the
            customer.
          </DialogDescription>
          <StepTrail phase={phase} />

          {phase === "configure" && (
            <ConfigureStep
              baseline={baseline}
              draft={draft}
              errors={errors}
              changes={changes}
              limits={limits}
              limitsError={
                limitsQuery.isError
                  ? messageOf(
                      limitsQuery.error,
                      "The plan's limits could not be read.",
                    )
                  : null
              }
              loadError={loadError}
              priceError={priceError}
              onDraftChange={setDraft}
              onRecheck={() => {
                if (limitsQuery.isError) {
                  void limitsQuery.refetch();
                }
                void readLiveConfiguration();
              }}
              rechecking={quoteMutation.isPending || limitsQuery.isFetching}
              onRecheckPowerState={() => void recheckPowerState()}
              onShutDown={() => void shutDownVm()}
              stopping={stopping}
              powerNote={powerNote}
            />
          )}

          {phase === "confirm" && quote && (
            <ConfirmStep
              quote={quote}
              runError={runError}
              blocked={blockedByPowerState}
              onRecheck={() => {
                setPhase("configure");
                void readLiveConfiguration();
              }}
              rechecking={quoteMutation.isPending}
            />
          )}

          {phase === "running" && <RunningStep />}

          {phase === "done" && execution && (
            <OutcomeStep execution={execution} />
          )}
        </DialogBody>

        <DialogFooter className="gap-2 sm:justify-between">
          {phase === "configure" && (
            <>
              <p className="text-xs text-muted-foreground">
                {baseline?.checkedAt
                  ? `Infrastructure read ${formatDateTime(baseline.checkedAt)}`
                  : ""}
                {baseline?.vmUuid ? ` · VM ${baseline.vmUuid}` : ""}
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  disabled={!canPrice}
                  onClick={() => void priceChange()}
                  title={
                    changes.length > 0
                      ? undefined
                      : baseline?.providerReportsRunning
                        ? // Saying "change a value" under two locked fields reads as a broken form.
                          "Stop the VM first to change vCPU or memory — disks can still be grown"
                        : "Change a value first — these are the figures the infrastructure already reports"
                  }
                >
                  {quoteMutation.isPending ? (
                    <Spinner className="h-4 w-4" />
                  ) : (
                    <Receipt className="h-4 w-4" />
                  )}
                  Price this change
                </Button>
              </div>
            </>
          )}

          {phase === "confirm" && (
            <>
              <p className="text-xs text-muted-foreground">
                {quote?.checkedAt
                  ? `Priced against the infrastructure at ${formatDateTime(quote.checkedAt)}`
                  : ""}
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setRunError(null);
                    setPhase("configure");
                  }}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back to edit
                </Button>
                <Button
                  type="button"
                  disabled={!canConfirm}
                  onClick={() => void confirmChange()}
                  title={blockedByPowerState?.blockedReason ?? undefined}
                >
                  Confirm and change the VM
                </Button>
              </div>
            </>
          )}

          {phase === "running" && (
            <p className="text-xs text-muted-foreground">
              Changing the VM, then recording the result. This can take a few
              seconds.
            </p>
          )}

          {phase === "done" && (
            <>
              <div />
              <div className="flex gap-2">
                {execution && execution.outcome !== "Completed" ? (
                  <>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => onOpenChange(false)}
                    >
                      Close anyway
                    </Button>
                    <Button type="button" onClick={() => void confirmChange()}>
                      <RefreshCw className="h-4 w-4" />
                      Finish it now
                    </Button>
                  </>
                ) : (
                  <Button type="button" onClick={() => onOpenChange(false)}>
                    Close
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Where the operator is in the sequence, so "one more press" is visible rather than implied. */
function StepTrail({ phase }: { phase: Phase }) {
  const steps = [
    { key: "configure", label: "Configure" },
    { key: "confirm", label: "Review price" },
    { key: "done", label: "Done" },
  ] as const;

  const index =
    phase === "configure"
      ? 0
      : phase === "confirm" || phase === "running"
        ? 1
        : 2;

  return (
    <ol className="flex items-center gap-2 text-xs">
      {steps.map((step, position) => {
        const state =
          position < index ? "done" : position === index ? "current" : "todo";

        return (
          <li key={step.key} className="flex items-center gap-2">
            <span
              className={cn(
                "flex h-5 w-5 items-center justify-center rounded-full border text-[11px] font-medium",
                state === "current" &&
                  "border-primary bg-primary text-primary-foreground",
                state === "done" &&
                  "border-emerald-500/50 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
                state === "todo" && "border-border text-muted-foreground",
              )}
            >
              {state === "done" ? (
                <CheckCircle2 className="h-3 w-3" />
              ) : (
                position + 1
              )}
            </span>
            <span
              className={cn(
                state === "current" ? "font-medium" : "text-muted-foreground",
              )}
            >
              {step.label}
            </span>
            {position < steps.length - 1 && (
              <span className="h-px w-6 bg-border" aria-hidden="true" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** The form, and a plain list of what it is about to change. Nothing is priced here. */
function ConfigureStep({
  baseline,
  draft,
  errors,
  changes,
  limits,
  limitsError,
  loadError,
  priceError,
  onDraftChange,
  onRecheck,
  rechecking,
  onRecheckPowerState,
  onShutDown,
  stopping,
  powerNote,
}: {
  baseline: ResourceConfigChangeQuote | null;
  draft: DraftConfig | null;
  errors: Record<string, string>;
  changes: PendingChange[];
  /** The plan's range, or null until it has been read. */
  limits: ResourceConfigChangeLimits | null;
  limitsError: string | null;
  loadError: string | null;
  priceError: string | null;
  onDraftChange: (draft: DraftConfig) => void;
  /** Reads everything again from scratch — the way out of a read that failed. */
  onRecheck: () => void;
  rechecking: boolean;
  /** Re-reads the power state and keeps the form. */
  onRecheckPowerState: () => void;
  /** Shuts the VM down from here, then waits for the infrastructure to report it stopped. */
  onShutDown: () => void;
  stopping: boolean;
  /** What the last power check has to say, or null when there is nothing to add. */
  powerNote: string | null;
}) {
  const readError = loadError ?? limitsError;

  if (readError) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Could not read the configuration</AlertTitle>
        <AlertDescription>
          <p>{readError}</p>
          {/* The same re-read as Re-check below. Without it a failed first read left Cancel as the
              only move, and the operator reopened the dialog to ask for the identical call. */}
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-3"
            onClick={onRecheck}
            disabled={rechecking}
          >
            {rechecking && <Spinner className="mr-2" />}
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (!draft || !baseline || !limits) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  const current = baseline.current;
  const running = baseline.providerReportsRunning;

  const setCores = (value: string) => onDraftChange({ ...draft, cores: value });
  const setMemory = (value: string) =>
    onDraftChange({ ...draft, memoryMb: value });
  const setDisk = (device: string, value: string) =>
    onDraftChange({ ...draft, disks: { ...draft.disks, [device]: value } });

  const cores = parsePositive(draft.cores) ?? current.cores;
  const memoryMb = parsePositive(draft.memoryMb) ?? current.memoryMb;
  // The plan's disk range bounds every disk together, so what one can still take is what the
  // others have left.
  const diskTotal = draftDiskTotal(current, draft);
  const diskRoom = limits.diskGb.max - diskTotal;

  return (
    <div className="space-y-4">
      {priceError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>That configuration was refused</AlertTitle>
          <AlertDescription>{priceError}</AlertDescription>
        </Alert>
      )}

      {/* One block per call the change makes to the infrastructure, so what is grouped on screen is
          what is applied together. */}
      <ConfigZone
        title="vCPU and memory"
        call="PATCH cloud-vm"
        hint="One call for both. The VM must be stopped. Either can go up or down."
      >
        {running && (
          <div className="flex flex-col gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 sm:flex-row sm:items-center">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-background text-amber-500">
              <Power className="h-4 w-4" aria-hidden />
            </span>
            <div className="min-w-0 flex-1 space-y-0.5">
              <p className="text-sm font-medium">The VM is running</p>
              <p className="text-xs text-muted-foreground">
                The infrastructure reports it as{" "}
                {baseline.providerPowerState ?? "running"}, and it refuses a
                vCPU or memory change while it runs — it accepts the call and
                changes nothing, so those two are locked. Disks can still be
                grown.
              </p>
              {stopping && (
                <p className="text-xs text-muted-foreground">
                  Waiting for the infrastructure to report it stopped.
                </p>
              )}
              {powerNote && !stopping && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  {powerNote}
                </p>
              )}
            </div>
            <div className="flex shrink-0 gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={rechecking || stopping}
                onClick={onRecheckPowerState}
              >
                {rechecking && !stopping ? (
                  <Spinner className="h-4 w-4" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Re-check
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={stopping}
                onClick={onShutDown}
              >
                {stopping ? (
                  <Spinner className="h-4 w-4" />
                ) : (
                  <Power className="h-4 w-4" aria-hidden />
                )}
                {stopping ? "Shutting down…" : "Shut down"}
              </Button>
            </div>
          </div>
        )}

        <SpecRow
          id="config-cores"
          icon={Cpu}
          label="vCPU"
          hint={`Currently ${current.cores}. This plan allows ${limits.cores.min} to ${limits.cores.max}.`}
          error={errors.cores}
          disabled={running}
          control={
            <div className="flex gap-1.5">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 shrink-0"
                aria-label="One fewer vCPU"
                disabled={running || cores <= limits.cores.min}
                onClick={() =>
                  setCores(String(Math.max(limits.cores.min, cores - 1)))
                }
              >
                <Minus className="h-4 w-4" />
              </Button>
              <Input
                id="config-cores"
                type="number"
                min={limits.cores.min}
                max={limits.cores.max}
                inputMode="numeric"
                className="h-8 w-20 text-center"
                disabled={running}
                value={draft.cores}
                onChange={(event) => setCores(event.target.value)}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 shrink-0"
                aria-label="One more vCPU"
                disabled={running || cores >= limits.cores.max}
                onClick={() =>
                  setCores(String(Math.min(limits.cores.max, cores + 1)))
                }
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          }
        >
          <OptionTiles
            label="vCPU"
            options={stepsWithin(
              CPU_STEPS,
              limits.cores.min,
              limits.cores.max,
              current.cores,
            )}
            value={cores}
            current={current.cores}
            format={(value) => `${value} vCPU`}
            disabled={running}
            onPick={(value) => setCores(String(value))}
          />
        </SpecRow>

        <SpecRow
          id="config-memory"
          icon={MemoryStick}
          label="Memory (MB)"
          hint={`Currently ${current.memoryMb} MB. This plan allows ${limits.memoryGb.min} to ${limits.memoryGb.max} GB.`}
          error={errors.memoryMb}
          disabled={running}
          control={
            <Input
              id="config-memory"
              type="number"
              min={limits.memoryGb.min * 1024}
              max={limits.memoryGb.max * 1024}
              step={1024}
              inputMode="numeric"
              className="h-8 w-32 text-center"
              disabled={running}
              value={draft.memoryMb}
              onChange={(event) => setMemory(event.target.value)}
            />
          }
        >
          <OptionTiles
            label="Memory"
            options={stepsWithin(
              MEMORY_STEPS_MB,
              limits.memoryGb.min * 1024,
              limits.memoryGb.max * 1024,
              current.memoryMb,
            )}
            value={memoryMb}
            current={current.memoryMb}
            format={formatMemoryMb}
            disabled={running}
            onPick={(value) => setMemory(String(value))}
          />
        </SpecRow>
      </ConfigZone>

      <ConfigZone
        title="Grow a disk"
        call="PATCH cloud-vm/disk"
        hint={`One call per disk. Works while the VM runs. Disks only grow. All disks together: ${diskTotal} of the ${limits.diskGb.max} GB this plan allows.`}
      >
        {current.disks.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            The infrastructure reports no attached disks for this VM.
          </p>
        ) : (
          current.disks.map((disk) => {
            const typed =
              parsePositive(draft.disks[disk.device] ?? "") ?? disk.sizeGb;
            // The current size, then every listed size above it the plan still has room for.
            const sizes = [
              disk.sizeGb,
              ...DISK_STEPS_GB.filter(
                (size) => size > disk.sizeGb && size <= typed + diskRoom,
              ),
            ];

            return (
              <SpecRow
                key={disk.device}
                id={`config-disk-${disk.device}`}
                icon={HardDrive}
                label={disk.device}
                badge={
                  disk.billedSeparately && (
                    <Badge variant="outline" className="gap-1 font-normal">
                      <Lock className="h-3 w-3" />
                      Separate resource
                    </Badge>
                  )
                }
                hint={
                  disk.billedSeparately
                    ? `${disk.sizeGb} GB, billed as a storage resource of its own — resize it from that resource.`
                    : `Currently ${disk.sizeGb} GB. Cannot be reduced.`
                }
                error={errors[disk.device]}
                control={
                  <Input
                    id={`config-disk-${disk.device}`}
                    type="number"
                    // Growth only — storage cannot shrink a volume, so the floor is what exists today.
                    min={disk.sizeGb}
                    max={typed + Math.max(0, diskRoom)}
                    inputMode="numeric"
                    className="h-8 w-28 text-center"
                    disabled={disk.billedSeparately}
                    value={draft.disks[disk.device] ?? String(disk.sizeGb)}
                    onChange={(event) =>
                      setDisk(disk.device, event.target.value)
                    }
                  />
                }
              >
                {!disk.billedSeparately && sizes.length > 1 && (
                  <OptionTiles
                    label={disk.device}
                    options={sizes}
                    value={typed}
                    current={disk.sizeGb}
                    format={(value) => `${value} GB`}
                    onPick={(value) => setDisk(disk.device, String(value))}
                  />
                )}
              </SpecRow>
            );
          })
        )}
      </ConfigZone>

      <PendingChangeList changes={changes} />
    </div>
  );
}

/** Memory as an operator reads it: whole gigabytes where it is one, megabytes otherwise. */
function formatMemoryMb(memoryMb: number): string {
  return memoryMb % 1024 === 0 ? `${memoryMb / 1024} GB` : `${memoryMb} MB`;
}

/**
 * One block per call a configuration change makes to the infrastructure — `PATCH cloud-vm` for vCPU
 * and memory together, `PATCH cloud-vm/disk` once per disk — with the condition that call carries.
 */
function ConfigZone({
  title,
  call,
  hint,
  children,
}: {
  title: string;
  /** The provider call behind the block, as the infrastructure team names it. */
  call: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <section
      aria-label={title}
      className="space-y-3 rounded-lg border p-3 sm:p-4"
    >
      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-x-3">
          <h3 className="text-sm font-semibold">{title}</h3>
          <code className="text-[11px] text-muted-foreground">{call}</code>
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
      </div>
      {children}
    </section>
  );
}

/**
 * One button per size worth a single press, with the VM's current one tagged. The exact-value box
 * beside the row's label stays the way to any figure in between.
 */
function OptionTiles({
  label,
  options,
  value,
  current,
  format,
  disabled = false,
  onPick,
}: {
  /** What the sizes are of ("vCPU", a disk's device); the group is named "<label> sizes". */
  label: string;
  options: number[];
  value: number;
  current: number;
  format: (value: number) => string;
  disabled?: boolean;
  onPick: (value: number) => void;
}) {
  return (
    <div
      role="group"
      // Not the bare label: the exact-value box beside the row already answers to that name.
      aria-label={`${label} sizes`}
      className="grid grid-cols-4 gap-1.5 sm:grid-cols-8"
    >
      {options.map((option) => {
        const selected = option === value;
        return (
          <button
            key={option}
            type="button"
            aria-pressed={selected}
            disabled={disabled}
            onClick={() => onPick(option)}
            className={cn(
              "flex h-11 cursor-pointer flex-col items-center justify-center rounded-lg border bg-background px-1 text-xs tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring enabled:hover:border-primary/50 enabled:hover:bg-accent/40 disabled:cursor-not-allowed",
              selected &&
                "border-primary bg-primary/10 font-semibold text-primary ring-2 ring-primary/20 enabled:hover:border-primary enabled:hover:bg-primary/10",
            )}
          >
            <span>{format(option)}</span>
            {option === current && (
              <span className="text-[10px] font-normal leading-tight text-muted-foreground">
                Current
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * One figure of the configuration: its label with the exact-value box beside it, the one-press
 * sizes under it, then its hint or, when there is one, its error in the hint's place.
 */
function SpecRow({
  id,
  icon: Icon,
  label,
  badge,
  hint,
  error,
  disabled,
  control,
  children,
}: {
  id: string;
  icon: typeof Cpu;
  label: string;
  badge?: React.ReactNode;
  hint: string;
  error?: string;
  disabled?: boolean;
  /** The exact-value input, for a figure the one-press sizes do not list. */
  control: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className={cn("space-y-1.5", disabled && "opacity-60")}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label htmlFor={id} className="flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
          {label}
          {badge}
        </Label>
        {control}
      </div>
      {children}
      <p
        className={cn(
          "text-xs",
          error ? "text-destructive" : "text-muted-foreground",
        )}
      >
        {error ?? hint}
      </p>
    </div>
  );
}

/** What the form is about to change, before a single call is made. */
function PendingChangeList({ changes }: { changes: PendingChange[] }) {
  if (changes.length === 0) {
    return (
      <Alert>
        <CheckCircle2 className="h-4 w-4" />
        <AlertTitle>Nothing to change yet</AlertTitle>
        <AlertDescription>
          These are the figures the infrastructure already reports. Change one,
          then price it.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <p className="text-sm font-medium">
        {changes.length === 1 ? "1 change" : `${changes.length} changes`} to
        price
      </p>
      <ul className="space-y-1">
        {changes.map(({ key, label, unit, from, to, Icon }) => (
          <li key={key} className="flex items-center gap-2 text-sm">
            <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="font-medium">{label}</span>
            <span className="tabular-nums text-muted-foreground">
              {formatValue(from, unit)}
            </span>
            <ArrowRight className="h-3 w-3 text-muted-foreground" />
            <span className="tabular-nums font-medium">
              {formatValue(to, unit)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The price, and the one button that carries the whole change through. */
function ConfirmStep({
  quote,
  runError,
  blocked,
  onRecheck,
  rechecking,
}: {
  quote: ResourceConfigChangeQuote;
  runError: string | null;
  /** The first block the provider will not take as things stand, or null when it will take them all. */
  blocked: ResourceConfigChangeBlock | null;
  onRecheck: () => void;
  rechecking: boolean;
}) {
  return (
    <div className="space-y-4">
      {runError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>The VM was not changed</AlertTitle>
          <AlertDescription>{runError}</AlertDescription>
        </Alert>
      )}

      {blocked && (
        <Alert className="border-amber-500/40 bg-amber-500/10 [&>svg]:text-amber-500">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Stop the VM before applying this</AlertTitle>
          <AlertDescription>
            <span className="block">
              {blocked.blockedReason}{" "}
              {quote.providerPowerState
                ? `The infrastructure reports it as ${quote.providerPowerState}.`
                : null}
            </span>
            <span className="mt-1 block">
              The price below is still what this change costs — stop the VM from
              its power controls, then re-check and confirm.
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2"
              disabled={rechecking}
              onClick={onRecheck}
            >
              {rechecking ? (
                <Spinner className="h-4 w-4" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Re-check power state
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {quote.blocks.length === 0 && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>The infrastructure already runs this</AlertTitle>
          <AlertDescription>
            Nothing is left to apply — usually because an earlier attempt got
            this far and stopped before recording it. Confirming writes the
            record and the price and finishes the job.
          </AlertDescription>
        </Alert>
      )}

      <PriceBreakdown quote={quote} />

      {/* Stated once, plainly, next to the button that looks like a checkout and is not one. */}
      <p className="text-xs text-muted-foreground">
        Confirming changes the VM, records the result on the resource and moves
        it onto its new monthly price in one step. Nothing is collected — the
        total above is the difference up to the expiry date, to quote or invoice
        separately.
      </p>
    </div>
  );
}

/** What each block costs, and what to quote the customer. */
function PriceBreakdown({ quote }: { quote: ResourceConfigChangeQuote }) {
  const currency = quote.currency;

  // The catalogue's opinion of the current configuration and what the resource actually charges are
  // two different numbers whenever prices have moved since it was sold. The difference below is
  // worked out from the first; saying so beats letting an operator read it as the second.
  const recordedDiffers =
    quote.recordedMonthlyPrice !== quote.currentMonthlyPrice;

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div className="flex items-center justify-between">
        <h4 className="flex items-center gap-1.5 text-sm font-medium">
          <Receipt className="h-4 w-4 text-muted-foreground" />
          Price for this change
          {quote.productLine && (
            <Badge variant="outline" className="ml-1 font-normal">
              {quote.productLine}
            </Badge>
          )}
        </h4>
      </div>

      {quote.blocks.length > 0 && (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[38%]">Block</TableHead>
                <TableHead className="w-[24%]">Change</TableHead>
                <TableHead className="w-[19%] text-right">Per month</TableHead>
                <TableHead className="w-[19%] text-right">Due now</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quote.blocks.map((block) => (
                <BlockRow key={block.key} block={block} currency={currency} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Separator />

      <dl className="space-y-1 text-sm">
        <div className="flex justify-between">
          <dt className="text-muted-foreground">Monthly price</dt>
          <dd className="tabular-nums">
            {formatCurrency(quote.currentMonthlyPrice, currency)}
            <ArrowRight className="mx-1 inline h-3 w-3 text-muted-foreground" />
            <span className="font-medium">
              {formatCurrency(quote.newMonthlyPrice, currency)}
            </span>
          </dd>
        </div>

        {recordedDiffers && (
          <div className="flex justify-between">
            <dt className="text-muted-foreground">
              Recorded on the resource today
              <span className="block text-xs">
                Rates have moved since this was sold — the difference below uses
                today's rates.
              </span>
            </dt>
            <dd className="tabular-nums">
              {formatCurrency(quote.recordedMonthlyPrice, currency)}
            </dd>
          </div>
        )}

        <div className="flex justify-between">
          <dt className="text-muted-foreground">Monthly difference</dt>
          <dd className="tabular-nums">
            {formatDelta(quote.monthlyDifference, currency)}
          </dd>
        </div>

        {/* The same sum the client's upgrade wizard shows: a change is charged from today up to
            the expiry date, at what it adds per day. */}
        {quote.hasTerm ? (
          <div className="flex justify-between">
            <dt>
              {formatDelta(quote.dailyDifference, currency)}/day ×{" "}
              {quote.remainingDays} {quote.remainingDays === 1 ? "day" : "days"}
              <span className="block text-xs text-muted-foreground">
                {quote.expiresAt
                  ? `Until the expiry date, ${formatDateTime(quote.expiresAt)}.`
                  : null}
                {quote.termMonths > 1
                  ? ` At the ${quote.termMonths}-month term rate.`
                  : null}
              </span>
            </dt>
            <dd className="tabular-nums">
              {formatDelta(quote.amountDue, currency)}
            </dd>
          </div>
        ) : (
          <div className="flex justify-between">
            <dt className="text-muted-foreground">
              No expiry date ahead — quoted as one month's difference
            </dt>
            <dd className="tabular-nums">
              {formatDelta(quote.amountDue, currency)}
            </dd>
          </div>
        )}

        <div className="flex justify-between">
          <dt className="text-muted-foreground">VAT ({quote.vatRate}%)</dt>
          <dd className="tabular-nums">
            {formatCurrency(quote.tax, currency)}
          </dd>
        </div>

        <div className="flex justify-between border-t pt-1 text-base font-semibold">
          <dt>Total to quote the customer</dt>
          <dd className="tabular-nums">
            {formatCurrency(quote.total, currency)}
          </dd>
        </div>
      </dl>

      {quote.total < 0 && (
        <p className="text-xs text-muted-foreground">
          This change lowers the price. A refund is a separate decision and is
          not made here.
        </p>
      )}
    </div>
  );
}

/** One priced block, with the reason it cannot be applied when there is one. */
function BlockRow({
  block,
  currency,
}: {
  block: ResourceConfigChangeBlock;
  currency: string;
}) {
  const Icon = BLOCK_ICONS[block.kind];

  return (
    <TableRow
      className={cn(!block.canApply && "bg-amber-50/60 dark:bg-amber-500/10")}
    >
      <TableCell className="font-medium">
        <span className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
          <span>
            {block.label}
            {!block.canApply && block.blockedReason && (
              <span className="block text-xs font-normal text-amber-700 dark:text-amber-400">
                {block.blockedReason}
              </span>
            )}
          </span>
        </span>
      </TableCell>
      <TableCell className="tabular-nums">
        <span className="text-muted-foreground">
          {formatValue(block.currentValue, block.unit)}
        </span>
        <ArrowRight className="mx-1 inline h-3 w-3 text-muted-foreground" />
        <span className="font-medium">
          {formatValue(block.targetValue, block.unit)}
        </span>
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatDelta(block.monthlyDelta, currency)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatDelta(block.amountDue, currency)}
      </TableCell>
    </TableRow>
  );
}

/** The one screen with nothing to decide. */
function RunningStep() {
  return (
    <div className="flex items-center gap-3 rounded-lg border p-4">
      <Spinner className="h-5 w-5" />
      <div>
        <p className="text-sm font-medium">Changing the VM</p>
        <p className="text-xs text-muted-foreground">
          Applying the configuration, then recording the result and the new
          price. Leave this open.
        </p>
      </div>
    </div>
  );
}

/** Whether a given outcome left work behind, and how it should read on screen. */
const OUTCOME_TONE: Record<ConfigChangeOutcome, "good" | "warn" | "bad"> = {
  Completed: "good",
  PartiallyApplied: "warn",
  PendingInfrastructure: "warn",
  AppliedNotRecorded: "bad",
  AppliedNotPriced: "bad",
};

/** What happened, what it cost, and what — if anything — is left to do. */
function OutcomeStep({
  execution,
}: {
  execution: ResourceConfigChangeExecution;
}) {
  const tone = OUTCOME_TONE[execution.outcome] ?? "warn";
  const settlement = execution.settlement;

  return (
    <div className="space-y-4">
      <Alert
        className={cn(
          tone === "good" &&
            "border-emerald-500/40 bg-emerald-500/10 [&>svg]:text-emerald-500",
          tone === "warn" &&
            "border-amber-500/40 bg-amber-500/10 [&>svg]:text-amber-500",
        )}
        variant={tone === "bad" ? "destructive" : "default"}
      >
        {tone === "good" ? (
          <CheckCircle2 className="h-4 w-4" />
        ) : (
          <AlertTriangle className="h-4 w-4" />
        )}
        <AlertTitle>
          {tone === "good"
            ? "Configuration changed"
            : "The change did not finish"}
        </AlertTitle>
        <AlertDescription>
          {execution.summary}
          {execution.warning && (
            <span className="mt-1 block font-medium">{execution.warning}</span>
          )}
        </AlertDescription>
      </Alert>

      <ul className="space-y-2">
        {execution.steps.map((step) => (
          <li
            key={step.key}
            className={cn(
              "flex items-start gap-3 rounded-lg border p-3",
              step.succeeded
                ? "border-emerald-500/40 bg-emerald-500/5"
                : "border-destructive/40 bg-destructive/5",
            )}
          >
            <span className="mt-0.5">
              {step.succeeded ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              ) : (
                <XCircle className="h-4 w-4 text-destructive" />
              )}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium">
                {step.label}: {formatValue(step.previousValue, step.unit)}
                <ArrowRight className="mx-1 inline h-3 w-3 text-muted-foreground" />
                {formatValue(step.newValue, step.unit)}
              </span>
              {step.providerMessage && (
                <span className="block text-xs text-muted-foreground">
                  {step.providerMessage}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>

      {settlement && (
        <>
          <div className="rounded-lg border p-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Monthly price</span>
              <span className="tabular-nums">
                {formatCurrency(
                  settlement.previousMonthlyPrice,
                  settlement.currency,
                )}
                <ArrowRight className="mx-1 inline h-3 w-3 text-muted-foreground" />
                <span className="font-medium">
                  {formatCurrency(
                    settlement.newMonthlyPrice,
                    settlement.currency,
                  )}
                </span>
              </span>
            </div>
            <p className="pt-1 text-xs text-muted-foreground">
              The next renewal charges the new price.
            </p>
          </div>

          {/* Spelled out rather than implied: no money moved, and the operator is the only one who
              can act on the difference. */}
          {settlement.amountToInvoice > 0 ? (
            <Alert className="border-amber-500/40 bg-amber-500/10 [&>svg]:text-amber-500">
              <Receipt className="h-4 w-4" />
              <AlertTitle>
                {formatCurrency(
                  settlement.amountToInvoice,
                  settlement.currency,
                )}{" "}
                to invoice separately
              </AlertTitle>
              <AlertDescription>{settlement.note}</AlertDescription>
            </Alert>
          ) : (
            <p className="text-xs text-muted-foreground">{settlement.note}</p>
          )}
        </>
      )}

      {execution.comparison && (
        <ConfigComparison
          comparison={execution.comparison}
          heading="Record vs infrastructure"
        />
      )}
    </div>
  );
}

/**
 * The resource record next to what the infrastructure reports — the same comparison the
 * configuration-mismatch screen shows, rendered here so the operator can see the two agree without
 * leaving the dialog.
 */
function ConfigComparison({
  comparison,
  heading,
}: {
  comparison: ResourceSpecMismatch;
  heading: string;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium">{heading}</h4>
        {comparison.hasMismatch ? (
          <Badge variant="warning">Differs</Badge>
        ) : (
          <Badge variant="outline">Record matches infrastructure</Badge>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[34%]">Value</TableHead>
              <TableHead className="w-[33%]">
                <span className="flex items-center gap-1.5">
                  <Database className="h-3.5 w-3.5" />
                  WiCloud
                </span>
              </TableHead>
              <TableHead className="w-[33%]">
                <span className="flex items-center gap-1.5">
                  <Server className="h-3.5 w-3.5" />
                  Infrastructure
                </span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {comparison.fields.map((field) => {
              const differs = field.status === "Mismatch";
              return (
                <TableRow
                  key={field.key}
                  className={cn(
                    differs && "bg-amber-50/60 dark:bg-amber-500/10",
                  )}
                >
                  <TableCell className="font-medium">{field.label}</TableCell>
                  <TableCell
                    className={cn(
                      "tabular-nums",
                      differs && "text-muted-foreground line-through",
                    )}
                  >
                    {formatValue(field.wiCloudValue, field.unit)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "tabular-nums",
                      differs &&
                        "font-semibold text-amber-700 dark:text-amber-400",
                    )}
                  >
                    {formatValue(field.providerValue, field.unit)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
